import { ProgressBar } from '@appsumo/dorado-react';
import { Fragment, memo, useCallback, useMemo } from 'react';
import { DISCUSSION_TACO_RATING_CHANGE_EVENT_NAME } from '~/lib/discussions/constants';
import { TacoRatingFilter } from '~/lib/discussions/types';
import { useEventDispatch } from '~/lib/events';
import { pluralize } from '~/lib/util';
import { DealReview } from '~/types/deal';

interface RatingCountMap {
  rating: number;
  count: number;
}

function BreakoutRating({
  dealReview,
}: Readonly<{
  dealReview: DealReview;
}>) {
  const dispatchEvent = useEventDispatch();
  const totalReviews = dealReview.review_count;

  const individualRatingCounts: RatingCountMap[] = useMemo(
    () => [
      { rating: 5, count: dealReview.review_count_5_tacos || 0 },
      { rating: 4, count: dealReview.review_count_4_tacos || 0 },
      { rating: 3, count: dealReview.review_count_3_tacos || 0 },
      { rating: 2, count: dealReview.review_count_2_tacos || 0 },
      { rating: 1, count: dealReview.review_count_1_tacos || 0 },
    ],
    [dealReview],
  );

  const handleTacoRatingChange = useCallback(
    (rating: TacoRatingFilter) => {
      dispatchEvent(DISCUSSION_TACO_RATING_CHANGE_EVENT_NAME, rating);
    },
    [dispatchEvent],
  );

  const tacoRatings = useMemo(
    () =>
      individualRatingCounts.map((ratingObj) => {
        const { rating, count } = ratingObj;
        const tacoRatingKey = `taco-rating-${rating}`;
        const tacoRating = `${rating} ${pluralize(rating, 'taco')}`;
        return (
          <Fragment key={tacoRatingKey}>
            <button
              onClick={() =>
                handleTacoRatingChange(String(rating) as TacoRatingFilter)
              }
              className="flex self-start text-blue-600 hover:underline"
              data-testid={`${tacoRatingKey}-button`}
            >
              {tacoRating}
            </button>
            <ProgressBar
              max={100}
              value={(count / totalReviews) * 100}
              variant="primary"
              className="h-4 rounded"
            />
            <span
              className="flex justify-end"
              data-testid={`${tacoRatingKey}-count`}
            >
              <strong>{Intl.NumberFormat().format(count)}</strong>
            </span>
          </Fragment>
        );
      }),
    [individualRatingCounts, totalReviews, handleTacoRatingChange],
  );

  return (
    <div className="mt-6 rounded border border-gravel p-4">
      <h4 className="mb-4 text-xl font-semibold">Taco ratings</h4>
      <div className="mt-4 grid grid-cols-breakout-ratings items-center gap-x-4 gap-y-5">
        {tacoRatings}
      </div>
    </div>
  );
}

export default memo(BreakoutRating);
